import ApiGateway from 'lib/api-gateway';
import AppConfig from 'helpers/config';
import EmployeeEntity from 'employees/entities/employee';

class EmployeeAPIGateway {

  toString() {
    return 'EmployeeAPIGateway';
  }

  /**
   * @function employeeList
   * @description Fetches the list of employees from the server and maps the data to EmployeeEntity objects.
   * @param {Object} params - Parameters to filter employee list.
   * @returns {Object} - Response data with list of employees.
   */
  async employeeList(params) {
    const listConfig =  AppConfig.server.apiHost + '/employee/';
    const apiGateway = new ApiGateway(listConfig);
    const response = await apiGateway.load(params);
    const entity = response.data.results.map(i => new EmployeeEntity(i));
    return { ...response.data, results: entity };

  }

  /**
   * @function getRoleList
   * @description Fetches the list of roles available in the company.
   * @param {Object} params - Parameters for role list.
   * @returns {Array} - List of roles.
   */
  async getRoleList(params) {
    const roleListConfig =  AppConfig.server.apiHost + '/company/employee/role/';
    const apiGateway = new ApiGateway(roleListConfig);
    const response = await apiGateway.load(params);
    const roleList = response.data;
    return roleList;
  }

  /**
   * @function getDepartmentList
   * @description Fetches the list of departments from the server.
   * @param {Object} params - Parameters to filter department list.
   * @returns {Array} - List of departments.
   */
  async getDepartmentList(params) {
    const departmentListConfig =  AppConfig.server.apiHost + '/company/departments/'; 
    const apiGateway = new ApiGateway(departmentListConfig);
    const response = await apiGateway.load(params);
    const departmentList = response.data;
    return departmentList;
  }

  /**
   * @function getCountryList
   * @description Fetches the list of countries from the server.
   * @param {Object} params - Parameters for country list.
   * @returns {Array} - List of countries.
   */
  async getCountryList(params) {
    const countryListConfig =  AppConfig.server.apiHost + '/address/country/';
    const apiGateway = new ApiGateway(countryListConfig);
    const response = await apiGateway.load(params);
    const countryList = response.data;
    return countryList;
  }

  /**
   * @function getStateList
   * @description Fetches the list of states from the server.
   * @param {Object} params - Parameters for state list.
   * @returns {Array} - List of states.
   */
  async getStateList(params) {
    const stateListConfig =  AppConfig.server.apiHost + '/address/state/'; 
    const apiGateway = new ApiGateway(stateListConfig);
    const response = await apiGateway.load(params);
    const stateList = response.data;
    return stateList;
  }

  /**
   * @function getAllSubCompanies
   * @description Fetches the list of all sub-companies from the server.
   * @param {Object} params - Parameters for sub-companies list.
   * @returns {Array} - List of sub-companies.
   */
  async getAllSubCompanies(params) {
    const subcompanyListConfig =  AppConfig.server.apiHost + '/company/all/';
    const apiGateway = new ApiGateway(subcompanyListConfig);
    const response = await apiGateway.load(params);
    const subcompanyList = response.data;
    return subcompanyList;
  }

  /**
   * @function getTeamList
   * @description Fetches the list of teams from the server.
   * @param {Object} params - Parameters for team list.
   * @returns {Array} - List of teams.
   */
  async getTeamList(params) {
    const teamListConfig =  AppConfig.server.apiHost + '/company/teams/';
    const apiGateway = new ApiGateway(teamListConfig);
    const response = await apiGateway.load(params);
    const teamList = response.data;
    return teamList;
  }

  /**
   * @function getMultiTeamList
   * @description Fetches the list of multiple teams from the server.
   * @param {Object} params - Parameters for multiple teams list.
   * @returns {Array} - List of multiple teams.
   */
  async getMultiTeamList(params) {
    const teamListConfig = AppConfig.server.apiHost + '/company/teams/';
    const apiGateway = new ApiGateway(teamListConfig);
    const response = await apiGateway.load(params);
    const multiTeamList = response.data;
    return multiTeamList;
  }

  /**
   * @function getMultiDepartmentList
   * @description Fetches the list of multiple departments from the server.
   * @param {Object} params - Parameters for multiple departments list.
   * @returns {Array} - List of multiple departments.
   */
  async getMultiDepartmentList(params) {
    const departmentListConfig = AppConfig.server.apiHost + '/company/departments/';
    const apiGateway = new ApiGateway(departmentListConfig);
    const response = await apiGateway.load(params);
    const multiDepartmentList = response.data;
    return multiDepartmentList;
  }

  /**
   * @function addEmployeeTags
   * @description Adds tags to an employee.
   * @param {Object} params - Parameters for adding employee tags.
   * @returns {Object} - Added tag data.
   */
  async addEmployeeTags(params) {
    const addTag =  AppConfig.server.apiHost + '/job/tags/';
    const apiGateway = new ApiGateway(addTag);
    const response = await apiGateway.save(params);
    const data = response.data;
    return data;
  }

  /**
   * @function createEmployee
   * @description Creates a new employee with the given details.
   * @param {Object} employee - Employee details to be created.
   * @returns {Object} - Created employee entity.
   */
  async createEmployee(employee) {
    const employeeReqObj = {
      ...employee,
      user: {
        first_name: employee.first_name,
        last_name: employee.last_name,
        password: employee.password,
        
      },
      company_id: employee.company,
      department: employee.department,
      team: employee.team && employee.team,
      employee_role: employee.employee_role,
      email : employee.email,
      title: employee.title,
    }

    const createEmployeeConfig =  AppConfig.server.apiHost + '/employee/';
    const apiGateway = new ApiGateway(createEmployeeConfig);
    const response = await apiGateway.save(employeeReqObj);
    const employeeEntity = new EmployeeEntity(response.data);
    return employeeEntity;
  }

  /**
   * @function updateEmployee
   * @description Updates an existing employee with new details.
   * @param {Object} employee - Employee data to be updated.
   * @param {boolean} invite - Invitation status for the employee.
   * @returns {Object} - Updated employee entity.
   */
  async updateEmployee(employee, invite) {
    const employeeReqObj = {
      ...employee,
      user: {
        first_name: employee.first_name,
        last_name: employee.last_name,
        password: employee.password,
      },
      phone_ext: employee.phone_ext ? employee.phone_ext : '+1',
      company_id: employee.company,
      department: employee.department ? employee.department : 0,
      team: employee.team ? employee.team : 0,
      employee_role: employee.employee_role,
      username : employee.username,
      invite : invite,
      state: employee.state ? employee.state : 0,
      jobs_present : employee.jobs_present,
      country: employee.country ? employee.country : 0,
    }
    const updateEmployeeConfig =  AppConfig.server.apiHost + '/employee/' + employee.id + '/';
    const apiGateway = new ApiGateway(updateEmployeeConfig);
    const response = await apiGateway.update(employeeReqObj);
    const employeeEntity = new EmployeeEntity(response.data);
    return employeeEntity;
  }

  /**
   * @function getEmployee
   * @description Fetches details of a specific employee by ID.
   * @param {string} employeeId - The ID of the employee.
   * @returns {Object} - Employee entity.
   */
  async getEmployee(employeeId) {
    const employeeConfig =  AppConfig.server.apiHost + '/employee/' + employeeId + '/'; 
    const apiGateway = new ApiGateway(employeeConfig);
    const response = await apiGateway.load();
    const employeeEntity = new EmployeeEntity(response.data);
    return employeeEntity;
  }

  /**
   * @function deleteMarkedTags
   * @description Deletes specified tags by ID.
   * @param {string} id - Tag ID.
   * @param {Object} data - Data to be passed with the deletion request.
   * @returns {void}
   */
  async deleteMarkedTags(id, data) {
    const tagsConfig =  AppConfig.server.apiHost + '/job/tags/' + id + '/'; 
    const apiGateway = new ApiGateway(tagsConfig);
    const response = await apiGateway.delete({...data});
  }

 /**
   * @function updateTagName
   * @description Updates the name of a specific tag by its ID.
   * @param {string} id - The ID of the tag to be updated.
   * @param {Object} data - Data containing the new tag name.
   * @returns {Object} - Response data from the server.
   */
  async updateTagName(id, data) {
    const tagsConfig =  AppConfig.server.apiHost + '/job/tags/' + id + '/'; 
    const apiGateway = new ApiGateway(tagsConfig);
    const response = await apiGateway.update({...data});
  }

/**
 * @function getEmployeesTags
 * @description Fetches a list of tags associated with employees.
 * @param {Object} params - Parameters for fetching tags.
 * @returns {Array} - List of employee tags.
 */
  async getEmployeesTags(params) {
    const tagsConfig =  AppConfig.server.apiHost + '/job/tags/';
    const apiGateway = new ApiGateway(tagsConfig);
    const response = await apiGateway.load(params);
    const tagsList = response.data;
    return tagsList;
  }

/**
 * @function getEmployeesPolicies
 * @description Fetches a list of policies related to employees.
 * @param {Object} params - Parameters for fetching policies.
 * @returns {Array} - List of employee policies.
 */
  async getEmployeesPolicies(params) {
    const tagsConfig =  AppConfig.server.apiHost + '/pto/policy/';
    const apiGateway = new ApiGateway(tagsConfig);
    const response = await apiGateway.load(params);
    const policyList = response?.data?.results;
    return policyList ? policyList : [];
  }

/**
 * @function getLoginLink
 * @description Fetches the login link for a specific employee by ID.
 * @param {string} employeeId - The ID of the employee.
 * @returns {Object} - Login link data.
 */
  async getLoginLink(employeeId) {
    const linkConfig =  AppConfig.server.apiHost + '/employee/' + employeeId + '/link/'; 
    const apiGateway = new ApiGateway(linkConfig);
    const response =  await apiGateway.load();
    const link = response.data;
    return link;
  }

/**
 * @function getInfoList
 * @description Fetches a list of documents or articles related to employee information.
 * @param {Object} params - Parameters for fetching information list.
 * @returns {Array} - List of documents or articles.
 */
  async getInfoList(params) {
    const infoListConfig =  AppConfig.server.apiHost + '/t2b/documents/kw-articles-list/';
    const apiGateway = new ApiGateway(infoListConfig);
    const response = await apiGateway.load(params);
    const infoList = response.data;
    return infoList;
  }

/**
 * @function getKioskAuditLogs
 * @description Fetches the audit logs related to face login failures for a specific employee.
 * @param {string} id - The ID of the employee.
 * @param {Object} params - Parameters for fetching audit logs.
 * @returns {Array} - List of kiosk audit logs.
 */
  async getKioskAuditLogs(id, params){
    const kioskListConfig =  AppConfig.server.apiHost + '/employee/' + id + '/face-login/fail/';
    const apiGateway = new ApiGateway(kioskListConfig);
    const response = await apiGateway.load(params);
    const kioskList = response.data;
    return kioskList;
  }

/**
 * @function postKioskAudits
 * @description Posts new kiosk audit data to the server.
 * @param {Object} params - Kiosk audit data to be posted.
 * @returns {Object} - Response data from the server.
 */
  async postKioskAudits(params) {
    const postKioskListConfig =  AppConfig.server.apiHost + '/t2b/faces/face/add-remove/';
    const apiGateway = new ApiGateway(postKioskListConfig);
    const response = await apiGateway.save(params);
    const data = response.data;
    return data;
  }

/**
 * @function updateStatus
 * @description Updates the status of multiple employees in bulk.
 * @param {Object} params - Parameters for updating employee status.
 * @returns {Object} - Response data from the server.
 */
  async updateStatus(params) {
    const postStatusConfig =  AppConfig.server.apiHost + '/employee/bulk/active/';
    const apiGateway = new ApiGateway(postStatusConfig);
    const response = await apiGateway.save(params);
    const data = response.data;
    return data;
  }

/**
 * @function export
 * @description Exports employee data from the server.
 * @param {Object} params - Parameters for exporting employee data.
 * @returns {Object} - Exported data.
 */
  async export(params) {
    const exportConfig =  AppConfig.server.apiHost + '/employee/employee-export/';
    const apiGateway = new ApiGateway(exportConfig);
    const response = await apiGateway.load(params);
    const data = response.data;
    return data;
  }

/**
 * @function resetPassword
 * @description Resets the password for an employee.
 * @param {Object} data - Data containing employee ID and new password.
 * @returns {Object} - Response indicating success or failure.
 */
  async resetPassword(data) {
    
    const resetConfig =  AppConfig.server.apiHost + '/employee/reset-password/';
    const apiGateway = new ApiGateway(resetConfig);
    const response = await apiGateway.save(data);
    const success = response.data;
    return success;
  }

/**
 * @function getSystemSettings
 * @description Fetches the system settings for the company.
 * @returns {Object} - System settings data.
 */
  async getSystemSettings() {
    const systemSettingConfig =  AppConfig.server.apiHost + '/company/settings/system/';
    const apiGateway = new ApiGateway(systemSettingConfig);
    const response = await apiGateway.load({ paginate: false });
    const systemSettingList = response.data;
    return systemSettingList;
  }

/**
 * @function getAuditLogs
 * @description Fetches audit logs related to company operations.
 * @param {Object} params - Parameters for fetching audit logs.
 * @returns {Array} - List of audit logs.
 */
  async getAuditLogs(params) {
    const auditListConfig =  AppConfig.server.apiHost + '/t2b/audits/audits/';
    const apiGateway = new ApiGateway(auditListConfig);
    const response = await apiGateway.load(params);
    const auditList = response.data;
    return auditList;
  }

/**
 * @function getAllJobsList
 * @description Fetches a list of all available jobs.
 * @param {Object} params - Parameters for fetching all jobs.
 * @returns {Array} - List of all jobs.
 */
    async getAllJobsList(params) {
      const allJobsListConfig =  AppConfig.server.apiHost + '/job/v2/';
      const apiGateway = new ApiGateway(allJobsListConfig);
      const response = await apiGateway.load(params);
      const allJobsList = response.data;
      return allJobsList;
    }

/**
 * @function getAssignedJobsList
 * @description Fetches a list of jobs assigned to employees.
 * @param {Object} params - Parameters for fetching assigned jobs.
 * @returns {Array} - List of assigned jobs.
 */
    async getAssignedJobsList(params) {
      const assignedJobsListConfig = AppConfig.server.apiHost + '/job/list/admin/';
      const apiGateway = new ApiGateway(assignedJobsListConfig);
      const response = await apiGateway.load(params);
      const assignedJobsList = response.data;
      return assignedJobsList;
    }

/**
 * @function getUnassignedJobsList
 * @description Fetches a list of jobs that are unassigned.
 * @param {Object} params - Parameters for fetching unassigned jobs.
 * @returns {Array} - List of unassigned jobs.
 */
    async getUnassignedJobsList(params) {
      const unAssignedJobsListConfig = AppConfig.server.apiHost + '/job/list/admin/';
      const apiGateway = new ApiGateway(unAssignedJobsListConfig);
      const response = await apiGateway.load(params);
      const unAssignedJobsList = response.data;
      return unAssignedJobsList;
    }

  /**
   * @function assignJobs
   * @description Assigns jobs to specific employees.
   * @param {Object} params - Parameters containing job IDs and employee IDs.
   * @returns {Object} - Response data from the server.
   */
    async assignJobs(params) {
      const job_idObj = {
        job_ids: params.job_ids,
        employee_ids: params.employee_ids,
      };
      const postAssignJobListConfig =  AppConfig.server.apiHost + '/job/employee/' + params.employee_id + '/';
      const apiGateway = new ApiGateway(postAssignJobListConfig);
      const response = await apiGateway.update(job_idObj);
      const data = response.data;
      return data;
    }

  /**
   * @function deleteEmployeeJob
   * @description Deletes a specific job assignment from an employee.
   * @param {Object} job - Job object containing the job ID.
   * @returns {Object} - Response data from the server.
   */
    async deleteEmployeeJob(job) {
      const deleteEmployeeJobConfig =  AppConfig.server.apiHost + '/job/' + job.id + '/employee/';
      const apiGateway = new ApiGateway(deleteEmployeeJobConfig);
      const response = await apiGateway.update(job);
      const data = response.data;
      return data;
    }

  /**
   * @function updateUserPreference
   * @description Updates user preferences for a specific employee.
   * @param {Object} params - Parameters containing user preference data.
   * @returns {Object} - Response data from the server.
   */
    async updateUserPreference(params) {
      const updateUserPreferenceConfig = AppConfig.server.apiHost + '/employee/user_preference/view/';
      const apiGateway = new ApiGateway(updateUserPreferenceConfig);
      const response = await apiGateway.update(params);
      const data = response.data;
      return data;
    }

  /**
   * @function getUserPreference
   * @description Fetches user preferences for a specific employee.
   * @param {Object} params - Parameters for fetching user preferences.
   * @returns {Object} - Employee preference data.
   */
    async getUserPreference(params) {
      const getUserPreferenceConfig = AppConfig.server.apiHost + '/employee/user_preference/view/';
      const apiGateway = new ApiGateway(getUserPreferenceConfig);
      const response = await apiGateway.load(params);
      const employeePreferenceData = response.data;
      return employeePreferenceData;
    }

  /**
   * @function enableResetBiometric
   * @description Enables the reset of biometric data (face recognition) for a specific employee.
   * @param {Object} employee - Employee object containing the employee ID.
   * @returns {void}
   */
    async enableResetBiometric(employee) {
      const enableResetBiometricConfig =  AppConfig.server.apiHost + `/employee/${employee.id}/reset-face/`;      
      const apiGateway = new ApiGateway(enableResetBiometricConfig);
      const response = await apiGateway.delete();
    }

  /**
   * @function enableResetPincode
   * @description Enables the reset of the pincode for a specific employee.
   * @param {Object} employee - Employee object containing the employee ID.
   * @returns {void}
   */
    async enableResetPincode(employee) {
      const enableResetPincodeConfig =  AppConfig.server.apiHost + `/employee/pincode/${employee.id}/?reset_pincode=true`;      
      const apiGateway = new ApiGateway(enableResetPincodeConfig);
      const response = await apiGateway.update();
    }

  /**
   * @function updateUserJobAssignment
   * @description Updates job assignments for a specific employee.
   * @param {Object} params - Parameters containing job assignments data.
   * @returns {Object} - Response data from the server.
   */
    async updateUserJobAssignment(params) {
      const updateUserPreferenceConfig = AppConfig.server.apiHost + '/job/remove_job/';
      const apiGateway = new ApiGateway(updateUserPreferenceConfig);
      const response = await apiGateway.update(params);
      const data = response.data;
      return data;
  }

  }
export default EmployeeAPIGateway;